import { PublicApi, PrivateApi } from '@shared/api-client'
import {
  getTimezone,
  getLangCodeByLanguage,
  toListOfNonNullishKeyValueObjects,
  toObjectOfNonNullishEntries,
} from '@shared/utils'
import Corporate from '@shared/data/models/corporate.js'
import { decodeAmpersands } from '@shared/utils/decodeAmpersands'
import { assignLocaleMessage } from '@shared/localization.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'

/** @typedef {import("axios").AxiosResponse} AxiosResponse */
/** @typedef {import("@shared/data/models/user").default} User */

const loadUserStore = async () =>
  (await import('@shared/stores/useUserStore.js')).default()

const loadFeatureFlagStore = async () =>
  (await import('@shared/stores/useFeatureFlagStore')).default()

/** @returns { Promise<User> } */
export const login = async (email, password) => {
  const response = await PublicApi.post('v2/auth/sign_in/', { email, password })
  return await (await loadUserStore()).storeSession(response)
}

/** @returns { Promise<User> } */
export const signUp = async ({
  email,
  password,
  firstName: first_name,
  lastName: last_name,
  phoneNumber: phone_number,
  dateOfBirth: date_of_birth,
  language,
  country,
  timezone,
  acceptedDataProtection: accepted_data_protection,
  acceptedMarketing: accepted_marketing,
  acceptedTracking: accepted_tracking,
  acceptedWebTracking: accepted_web_tracking,
  corporateId: corporate_id,
  corporateEligibilityData = {},
  fromApp: from_app, // from session storage
  verificationEmail,
  eligibilitySessionHash: eligibility_session_hash,
  lookupKey: solera_lookup_key,
  landingPageIncentive: landing_page_incentive,
  referrer,
  signupContext,
}) => {
  const featureFlagStore = await loadFeatureFlagStore()
  const preAssignedVariants = featureFlagStore.getPreSignupPayload()

  const response = await PublicApi.post('v2/auth/sign_up/', {
    email,
    password,
    first_name,
    date_of_birth,
    corporate_id,
    language,
    country,
    timezone,
    ...toObjectOfNonNullishEntries({
      accepted_data_protection,
      accepted_marketing,
      accepted_tracking,
      accepted_web_tracking,
      last_name,
      // '' must never reach the backend: it would be stored non-NULL and
      // analytics would count the user as having provided a phone number
      phone_number: phone_number || undefined,
      eligibility_session_hash,
      referrer,
      solera_lookup_key,
    }),
    signup_context: {
      web_entry_point_url: signupContext.webEntryPointUrl,
      web_entry_point_referrer_url: signupContext.webEntryPointReferrerUrl,
      how_did_you_hear_about_us: signupContext.howDidYouHearAboutUs,
    },
    feedback: [
      ...toListOfNonNullishKeyValueObjects({
        date_of_birth,
        from_app,
        email: verificationEmail,
        landing_page_incentive,
        ...corporateEligibilityData,
      }),
    ],
    pre_assigned_variants: preAssignedVariants,
  })
  return await (await loadUserStore()).storeSession(response)
}

/** @returns { Promise<User> } */
export const loginWithOneTimeAuthToken = async (token) => {
  const response = await PublicApi.post('/1fa/auth/sign_in/token', { token })
  return await (await loadUserStore()).storeSession(response)
}

export const getOneTimeAuthToken = (code_challenge) =>
  PrivateApi.get('v2/auth/token', {
    params: code_challenge ? { code_challenge } : undefined,
  })

export const uploadPrescriptionFile = (formData, params) => {
  params = Object(params) === params ? params : {}

  return PrivateApi.post('v2/user/prescriptions', formData, params)
}

const withReadableTitles = (response) => {
  response.data?.corporates?.forEach((corporate) => {
    corporate.title = decodeAmpersands(corporate.title)
  })
  return response
}

export const getPopularCorporates = ({ limit } = {}) =>
  PublicApi.get('v2/popular_corporates', {
    params: { ...(limit != null && { limit }) },
  }).then(withReadableTitles)

export const sendHcpInformation = ({ name, city, zip }) =>
  PrivateApi.post('v2/user/hcp_information', {
    name,
    city,
    ...(typeof zip === 'string' && zip.length > 0 ? { zip } : {}),
  })

export const sendSubscriptionActivationEntry = ({ type, attributes }) =>
  PrivateApi.post('v2/subscription_activation_entries', {
    data: {
      type,
      attributes,
    },
  })

/** @returns { Promise<User> } */
export const subscribeUserWithVoucher = async (code) => {
  await PrivateApi.post('v2/payment/subscribe_with_corporate_voucher', { code })
  return await getUser()
}

export const checkVoucher = (code) =>
  PublicApi.post('v2/check_corporate_voucher/', { code })

/** @returns { Promise<User> } */
export const submitActivationCode = async (code) => {
  await PrivateApi.post('v2/payment/subscribe_with_code', { code })
  return await getUser()
}

/**
 * @param { { key?: string, id?: number } } corporateRef
 * @returns { Promise<Corporate|null> } null when neither key nor id is
 *   provided and none is stored
 */
export const loadCorporate = async ({ key, id }) => {
  const corporateStore = useCorporateStore()
  const lang = (await loadUserStore()).lang

  let corporateInstance = corporateStore.corporate
  // Only fetch if not already loaded
  if (
    (key && corporateInstance?.key !== key) ||
    (id && corporateInstance?.id !== id)
  ) {
    const url = key
      ? `v2/content/get_corporate?key=${key}&language=${lang}&timezone=${getTimezone()}`
      : `v2/content/get_corporate?id=${id}&language=${lang}&timezone=${getTimezone()}`
    const response = await PublicApi.get(url)
    corporateInstance = new Corporate(response.data)
  }

  if (corporateInstance?.translations) {
    assignLocaleMessage(
      corporateInstance.translations,
      getLangCodeByLanguage(lang),
    )
  }
  if (corporateInstance) {
    corporateStore.corporate = corporateInstance
  }
  return corporateInstance
}

/** @returns { Promise<User> } */
export const getUser = async () => {
  const response = await PrivateApi.get('1fa/user')
  return await (await loadUserStore()).storeSession(response)
}

/**
 * Batch-upsert user consents. `consents` is an array of
 * `{ type: string, given: boolean }` entries; applied atomically server-side.
 * @returns { Promise<User> }
 */
export const updateUserConsents = async (consents) => {
  const response = await PrivateApi.patch('1fa/consents', { consents })
  return await (await loadUserStore()).storeSession(response)
}

/** @returns {Promise<Array<{featureFlagKey: string, enabled: boolean, experimentKey: string|null, variantKey: string|null}>>} */
export const getUserFeatureFlags = async () => {
  const response = await PrivateApi.get('v2/user/feature_flags')
  if (!Array.isArray(response?.data?.data)) return []

  return response.data.data.flatMap((featureFlag) => {
    if (
      typeof featureFlag?.feature_flag_key !== 'string' ||
      typeof featureFlag?.enabled !== 'boolean'
    ) {
      return []
    }

    return [
      {
        featureFlagKey: featureFlag.feature_flag_key,
        enabled: featureFlag.enabled,
        experimentKey:
          typeof featureFlag?.experiment_key === 'string'
            ? featureFlag.experiment_key
            : null,
        variantKey:
          typeof featureFlag?.variant_key === 'string'
            ? featureFlag.variant_key
            : null,
      },
    ]
  })
}

export const setUserEffectiveFeatureFlags = (featureFlags) => {
  const payload = Array.isArray(featureFlags)
    ? featureFlags.flatMap((featureFlag) =>
        typeof featureFlag?.featureFlagKey === 'string' &&
        typeof featureFlag?.enabled === 'boolean'
          ? [
              {
                feature_flag_key: featureFlag.featureFlagKey,
                enabled: featureFlag.enabled,
              },
            ]
          : [],
      )
    : []

  return PrivateApi.post('v2/user/effective_feature_flags', { data: payload })
}

/**
 * Every field is optional — nullish entries are dropped from the PATCH.
 * @param {{ firstName?: string, lastName?: string,
 *   acceptedDataProtection?: boolean, acceptedTracking?: boolean,
 *   phoneNumber?: string, corporateId?: number, dateOfBirth?: string,
 *   language?: string, abortSignal?: AbortSignal }} profile
 * @returns { Promise<User> }
 */
export const setUserProfile = async ({
  firstName: first_name,
  lastName: last_name,
  acceptedDataProtection: accepted_data_protection,
  acceptedTracking: accepted_tracking,
  phoneNumber: phone_number,
  corporateId: corporate_id,
  dateOfBirth: date_of_birth,
  language,
  abortSignal,
}) => {
  const response = await PrivateApi.patch(
    '1fa/profile',
    toObjectOfNonNullishEntries({
      first_name,
      last_name,
      accepted_data_protection,
      accepted_tracking,
      phone_number: phone_number || undefined,
      corporate_id,
      date_of_birth,
      language,
    }),
    { signal: abortSignal },
  )
  return await (await loadUserStore()).storeSession(response)
}

/** @returns { Promise<User> } */
export const setUserEmail = async (email) => {
  const response = await PrivateApi.post('v2/user/set_email', { email })
  return await (await loadUserStore()).storeSession(response)
}

export const checkCorporateEligibility = (corporateEligibilityData) =>
  PublicApi.get('v2/check_corporate_eligibility', {
    params: corporateEligibilityData,
  })

export const checkEligibilityWithEligibleToken = (
  eligibility_session_hash,
  eligible_token,
) =>
  PublicApi.post('v2/check_eligibility/with_token', {
    eligibility_session_hash,
    eligible_token,
  })

/**
 * Landing-page owned eligibility list verification.
 */
export const checkEligibilityWithList = ({
  landingPageSlug,
  eligibilitySessionHash,
  fields,
}) =>
  PublicApi.post('v2/check_eligibility/with_list', {
    landing_page_slug: landingPageSlug,
    eligibility_session_hash: eligibilitySessionHash,
    ...fields,
  })

/**
 * @param { string } locale of the faqs, 'en-US' or 'es-US'
 * @typedef {{ id: string, title: string, order: number, text: string }} Faq
 * @typedef {{ count: number, faqs: Faq[] }} FaqList
 * @returns { Promise<AxiosResponse<{ count: number, faqs: Faq[] }>> }: count of faqs and list of faqs
 */
export const getHelpscoutFaqs = async (locale) =>
  await PublicApi.get('v2/support/faqs', {
    params: { locale },
  })

export const submitSupportTicket = async (data) =>
  await PublicApi.post('v2/support/tickets', data)

export const getCheckups = () => PrivateApi.get('1fa/user_checkups')

export const submitSelfTest = ({ answers, partial } = {}) =>
  PrivateApi.post('v2/user/complete_self_test', {
    answers,
    ...(partial ? { partial: true } : {}),
  })

// MSK EU: the same submission with the copy the patient saw. Answers 201, or 204 on a retry.
export const createSelfTest = ({ language, answers, partial } = {}) =>
  PrivateApi.post('v2/user/self_test', {
    language,
    answers,
    ...(partial ? { partial: true } : {}),
  })

export const submitSelfTestReevaluations = (data) =>
  PrivateApi.post('v2/user/assessment_questionnaire_results', {
    assessment_questionnaire_results: {
      self_test_reevaluation: data,
    },
  })

export const submitCAT = (data) =>
  PrivateApi.post('v2/user/assessment_questionnaire_results', {
    assessment_questionnaire_results: {
      cat: data,
    },
  })

// Both renewal questionnaires go up in one call at the end of the flow. Each
// answer carries the option key plus the copy the user actually saw, so the
// backend can store what was shown alongside the scored option.
export const submitRenewalAssessment = ({ pgic, renewalTherapyFeedback }) =>
  PrivateApi.post('v2/user/assessment_questionnaire_results', {
    assessment_questionnaire_results: {
      pgic,
      renewal_therapy_feedback: renewalTherapyFeedback,
    },
  })

export const searchCorporate = (search, country, abortSignal) =>
  PublicApi.get('v2/search_corporate', {
    params: { search, ...(country ? { country } : {}) },
    signal: abortSignal,
  }).then(withReadableTitles)

/** @returns { Promise<User> } */
export const subscribeWithInsuranceToken = async (
  corporateKey,
  token,
  type,
) => {
  await PrivateApi.post('v2/payment/subscribe_with_insurance_token', {
    corporate_name: corporateKey,
    token,
    type,
  })
  return await getUser()
}

/** @returns { Promise<User> } */
export const subscribeWithEligibleToken = async (verificationData) => {
  await PrivateApi.post(
    'v2/payment/subscribe_with_eligible_token',
    verificationData,
  )
  return await getUser()
}

/** @returns { Promise<User> } */
export const subscribeWithVerificationData = async (verificationData) => {
  await PrivateApi.post(
    'v2/payment/subscribe_with_verification_data',
    verificationData,
  )
  return await getUser()
}

export const sendCrmEvent = ({ name, properties }) => {
  if (name === null || name === undefined) {
    return Promise.reject({
      id: 1,
      message: `sendCrmEvent: expected ${name} to be a string;`,
    })
  }

  const payload = { name }

  if (properties !== null && properties !== undefined) {
    payload.properties = properties
  }

  return PrivateApi.post('v2/user/send_crm_event', payload)
}

export const resetPassword = (reset_password_token, password, baseURL) =>
  PublicApi.post(
    'auth/password/change_with_reset_token',
    {
      reset_password_token,
      password,
    },
    { baseURL: baseURL || PublicApi.defaults.baseURL },
  )

export const searchDoctorPractice = (name, abortSignal, useVzd = false) =>
  PrivateApi.get('1fa/doctor_practices/search', {
    params: { name, ...(useVzd ? { vzd: '1' } : {}) },
    signal: abortSignal,
  })

export const loadDoctorPractice = (id) =>
  PrivateApi.get(`1fa/doctor_practices/${id}`)

export const submitDoctorOutreachRequest = ({
  firstName: first_name,
  lastName: last_name,
  dateOfBirth: date_of_birth,
  consentGivenAt: consent_given_at,
  prescriberId: doctor_practice_id,
  enteredPrescriberName: name,
  enteredPrescriberPlace: city,
  enteredPrescriberZipCode: zip,
}) => {
  return PrivateApi.post(
    '1fa/prescription_requests',
    toObjectOfNonNullishEntries({
      first_name,
      last_name,
      date_of_birth,
      consent_given_at,
      doctor_practice_id,
      doctor_practice_request: toObjectOfNonNullishEntries({
        name,
        city,
        zip,
      }),
    }),
  )
}
